/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { defineStore } from 'pinia'
import { ref, watch } from 'vue'

export const useUiStore = defineStore('ui', () => {
  const STORAGE_KEY = 'showInfo'
  const EARTAG_PREFIX_KEY = 'digiziege_eartag_prefix'
  const PREFIX_INFO_DISMISSED_KEY = 'digiziege_prefix_info_dismissed'
  const PREFIX_INFO_LAST_SHOWN_KEY = 'digiziege_prefix_info_last_shown'
  const PREFIX_INFO_SHOW_COUNT_KEY = 'digiziege_prefix_info_show_count'

  // Reading Initial Value on store
  let initial = true 
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored !== null) {
      initial = stored === 'true'
    }
  } catch (err) {
    console.warn('Could not read info state from localStorage:', err)
  }

  const showInfo = ref(initial)

  const toggleInfo = () => {
    showInfo.value = !showInfo.value
  }

  // safe changes automatically
  watch(showInfo, (val) => {
    try {
      localStorage.setItem(STORAGE_KEY, val.toString())
    } catch (err) {
      console.warn('Could not save info state to localStorage:', err)
    }
  }, { immediate: false })

  // Toast notification state
  const pendingToast = ref(null)

  /**
   * Queue a toast notification to be shown
   * @param {Object} notification
   * @param {string} notification.message - The message to display
   * @param {string} notification.type - The type: 'saved', 'error', 'warning', 'info', 'working'
   */
  function queueToast(message, type = 'saved') {
    pendingToast.value = { message, type }
  }

  /**
   * Consume the pending toast (clears it after retrieval)
   * @returns {Object|null} The pending toast or null
   */
  function consumeToast() {
    const toast = pendingToast.value
    pendingToast.value = null
    return toast
  }

  // Eartag prefix state and management
  const eartagPrefix = ref(null)

  /**
   * Load eartag prefix from localStorage
   * Format: "DE 0123 456" (11 characters)
   */
  function loadEartagPrefix() {
    try {
      const stored = localStorage.getItem(EARTAG_PREFIX_KEY)
      eartagPrefix.value = stored || null
      return stored
    } catch (err) {
      console.warn('Could not read eartag prefix from localStorage:', err)
      return null
    }
  }

  /**
   * Save eartag prefix to localStorage
   * @param {string} prefix - Format: "DE 0123 456"
   */
  function setEartagPrefix(prefix) {
    try {
      if (prefix) {
        localStorage.setItem(EARTAG_PREFIX_KEY, prefix)
        eartagPrefix.value = prefix
      } else {
        localStorage.removeItem(EARTAG_PREFIX_KEY)
        eartagPrefix.value = null
      }
    } catch (err) {
      console.warn('Could not save eartag prefix to localStorage:', err)
    }
  }

  /**
   * Check if eartag prefix is set
   * @returns {boolean}
   */
  function hasEartagPrefix() {
    return !!eartagPrefix.value
  }

  /**
   * Get current eartag prefix
   * @returns {string|null}
   */
  function getEartagPrefix() {
    if (!eartagPrefix.value) {
      loadEartagPrefix()
    }
    return eartagPrefix.value
  }

  /**
   * Assemble full eartag from prefix and suffix
   * @param {string} suffix - Last 5 digits
   * @returns {string} Full eartag (e.g., "DE 0123 456 12345")
   * @throws {Error} If prefix is not set
   */
  function assembleEartag(suffix) {
    const prefix = getEartagPrefix()
    if (!prefix) {
      throw new Error('Eartag prefix not set. Please configure in Settings.')
    }
    return `${prefix} ${suffix}`
  }

  // Load prefix on store initialization
  loadEartagPrefix()

  /**
   * Prefix Info Modal Management
   * Shows periodically to suggest setting up eartag prefix
   */

  /**
   * Check if user has permanently dismissed the prefix info modal
   */
  function isPrefixInfoDismissed() {
    try {
      const dismissed = localStorage.getItem(PREFIX_INFO_DISMISSED_KEY)
      return dismissed === 'true'
    } catch (err) {
      console.warn('Could not read prefix info dismissed state:', err)
      return false
    }
  }

  /**
   * Mark prefix info modal as permanently dismissed
   */
  function dismissPrefixInfo() {
    try {
      localStorage.setItem(PREFIX_INFO_DISMISSED_KEY, 'true')
    } catch (err) {
      console.warn('Could not save prefix info dismissed state:', err)
    }
  }

  /**
   * Check if prefix info modal should be shown
   * Shows first time, then periodically (every 5 times) if not dismissed
   * @returns {boolean}
   */
  function shouldShowPrefixInfo() {
    // Don't show if prefix is already set
    if (hasEartagPrefix()) {
      return false
    }

    // Don't show if user has dismissed permanently
    if (isPrefixInfoDismissed()) {
      return false
    }

    try {
      const showCount = parseInt(localStorage.getItem(PREFIX_INFO_SHOW_COUNT_KEY) || '0', 10)
      const lastShown = localStorage.getItem(PREFIX_INFO_LAST_SHOWN_KEY)

      // Show first time (count is 0)
      if (showCount === 0) {
        return true
      }

      // Show periodically (every 5th time)
      if (showCount % 5 === 0) {
        // Check if enough time has passed (at least 1 hour)
        if (lastShown) {
          const lastShownTime = new Date(lastShown).getTime()
          const now = new Date().getTime()
          const hourInMs = 60 * 60 * 1000
          
          if (now - lastShownTime < hourInMs) {
            return false
          }
        }
        return true
      }

      return false
    } catch (err) {
      console.warn('Could not check prefix info show state:', err)
      return false
    }
  }

  /**
   * Mark that prefix info modal was shown
   */
  function markPrefixInfoShown() {
    try {
      const currentCount = parseInt(localStorage.getItem(PREFIX_INFO_SHOW_COUNT_KEY) || '0', 10)
      localStorage.setItem(PREFIX_INFO_SHOW_COUNT_KEY, (currentCount + 1).toString())
      localStorage.setItem(PREFIX_INFO_LAST_SHOWN_KEY, new Date().toISOString())
    } catch (err) {
      console.warn('Could not save prefix info show state:', err)
    }
  }

  //Global header State with configurations
  const header = ref({
    title: '',
    subtitle: '',
    showBack: false,
    showAdd: false,
    addAction: null,
  })

  function setHeader(config = {}) {
    header.value = {
      title: config.title ?? '',
      subtitle: config.subtitle ?? '',
      showBack: config.showBack ?? false,
      showAdd: config.showAdd ?? false,
      addAction: config.addAction ?? null,
    }
  }

  function resetHeader() {
    header.value = {
      title: '',
      subtitle: '',
      showBack: false,
      showAdd: false,
      addAction: null,
    }
  }

  return { 
    showInfo, 
    toggleInfo, 
    pendingToast, 
    queueToast, 
    consumeToast, 
    header, 
    setHeader, 
    resetHeader,
    // Eartag prefix
    eartagPrefix,
    loadEartagPrefix,
    setEartagPrefix,
    hasEartagPrefix,
    getEartagPrefix,
    assembleEartag,
    // Prefix info modal
    isPrefixInfoDismissed,
    dismissPrefixInfo,
    shouldShowPrefixInfo,
    markPrefixInfoShown
  }
})
